import React, { useState, useRef, Fragment, useEffect, JSX } from "react";
import { Form, InputGroup } from "react-bootstrap";
import { getScaleFactor, getUnitLabel, LabelType } from "../../helpers/unitHelpers";
import { ITranslationDatabase, Settings } from "../../helpers/sharedTypes";

interface UserData {
  settings: Settings;
}

const allowNegativeNumberRegex = /[^\d.-]/g;

const validNumberRegex = /^-?\d+\.?\d*$/;

const onlyPositiveNumbers = /[^\d.]/g;

const removeInvalidTextFromNumberString = (
  inp: string | number,
  disablenegativeNumber: boolean = false,
  isInteger: boolean = false,
) => {
  if (!inp) {
    return inp;
  }
  if (typeof inp !== "string") {
    inp = inp.toString();
  }
  let withDot = inp.replaceAll(",", ".");
  if (isInteger) {
    withDot = withDot.replaceAll(".", "");
  }
  const reg = disablenegativeNumber ? onlyPositiveNumbers : allowNegativeNumberRegex;
  const stripped = withDot.replace(reg, "");
  const firstDot = stripped.indexOf(".");
  if (firstDot === 0) {
    return stripped.replace(".", "0.");
  }
  if (firstDot === -1) {
    return stripped;
  }

  if (firstDot === stripped.length - 1) {
    return stripped;
  }

  const secondDot = stripped.indexOf(".", firstDot + 1);
  if (secondDot === -1) {
    return stripped;
  }

  const upToDot = stripped.substring(0, firstDot);
  const afterDot = stripped.substring(firstDot + 1).replace(".", "");

  return upToDot + "." + afterDot;
};

type GetDisplayStringFunction = () => LabelType | undefined;

export interface UnitConverterType {
  fromInput: (x: number) => number;
  toInput: (x: number) => number;
  getDisplayString: GetDisplayStringFunction;
}

const makeUnitConverter = (
  toInput: (si: number) => number,
  fromInput: (x: number) => number,
  getDisplayString: GetDisplayStringFunction,
): UnitConverterType => {
  return {
    fromInput: fromInput,
    toInput: toInput,
    getDisplayString: getDisplayString,
  };
};

const makeScaledConverter = (scale: number, getDisplayString: GetDisplayStringFunction) => {
  return makeUnitConverter(
    (si) => {
      return si * scale;
    },
    (user) => {
      return user / scale;
    },
    getDisplayString,
  );
};

class UnitCreator {
  db: ITranslationDatabase;
  user: UserData | undefined;

  constructor(db: ITranslationDatabase, user: UserData | undefined) {
    this.db = db;
    this.user = user;
  }

  makeConverter(tag: string) {
    const scaleFactor = this.user
      ? getScaleFactor({
          db: this.db,
          tag: tag,
          settings: this.user.settings,
        })
      : 1.0;
    return makeScaledConverter(scaleFactor, () =>
      this.user
        ? getUnitLabel({
            db: this.db,
            tag: tag,
            settings: this.user.settings,
            style: "html",
          })
        : undefined,
    );
  }

  getDisplayHeader(tag: string, suffix?: string) {
    return (
      <span className="unit-header">
        [{this.makeConverter(tag).getDisplayString()}
        {suffix ? suffix : null}]
      </span>
    );
  }

  getDisplayHeaderMultiple(tags: string[]) {
    return (
      <span className="unit-header">
        [
        {tags.map((tag, i) => {
          return (
            <Fragment key={i}>
              {this.makeConverter(tag).getDisplayString()}
              {i < tags.length - 1 ? "," : ""}
            </Fragment>
          );
        })}
        ]
      </span>
    );
  }
}

const getUnitOrDefault = (unit: UnitConverterType | undefined | null) => {
  return (
    unit ||
    makeUnitConverter(
      (x) => x,
      (x) => x,
      () => "",
    )
  );
};

const useOutsideClick = (callback: () => void) => {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const handleClick = () => {
      callback();
    };
    document.addEventListener("click", handleClick, true);
    return () => {
      document.removeEventListener("click", handleClick, true);
    };
  }, [ref]);
  return ref;
};

const valueAsNumber = (value: number | string) => {
  let val = typeof value === "number" ? value : Number(value);

  val = Math.round(val * 1000) / 1000;

  return val;
};

/** Display value for a field that accepts exponential input: 6 significant digits, and exponential
 *  form ("1.5e9") for very large or very small magnitudes so they stay readable in a narrow field. */
const valueAsExponentialDisplay = (value: number): number | string => {
  if (!Number.isFinite(value)) return 0;
  const rounded = Number(value.toPrecision(6));
  if (rounded !== 0 && (Math.abs(rounded) >= 1e6 || Math.abs(rounded) < 1e-3)) {
    return rounded.toExponential().replace("e+", "e");
  }
  return rounded;
};

/** Parses user text that may be in exponential form ("1e9", "2,5E-3"). Returns NaN when invalid. */
const parseExponentialInput = (value: string | number, disableNegativeNumber: boolean): number => {
  let text = String(value).replaceAll(",", ".").trim();
  if (disableNegativeNumber) text = text.replace(/^-/, "");
  if (text === "") return 0;
  return Number(text);
};

const NUMBER_VAL = "number";
type NumberType = "number";
const TEXT_VAL = "text";
type TextType = "text";

type NumberOrString<T> = T extends NumberType ? number : T extends TextType ? string : never;

interface SXEditFieldPropsT<T> {
  displayUnit?: boolean;
  hover?: boolean;
  label?: JSX.Element | string;
  tableView?: boolean;
  unit?: UnitConverterType | undefined | null;
  val: NumberOrString<T>;
  type: T;
  disableNegativeNumber?: boolean;
  isInteger?: boolean;
  // accept and show numbers in exponential form, e.g. "1e9" (number fields only)
  allowExponent?: boolean;
  min?: number | undefined;
  instantupdate?: boolean;
  textAlign?: "left" | "right" | "center";
  disabled?: boolean;
  placeholder?: string;
  border?: boolean;
  fontSize?: string | number;
  isTextArea?: boolean;
  id?: string;
  dataTestId?: string;
  fullWidth?: boolean;
  disabledDisplay?: string;
  onUpdate?: (val: NumberOrString<T>, revertValue: () => void) => void;
  onOldUpdateCallback?: (val: string) => void;
}
type SXEditFieldProps = SXEditFieldPropsT<NumberType> | SXEditFieldPropsT<TextType>;

const SXEditField = (props: SXEditFieldProps) => {
  const unit = getUnitOrDefault(props.unit);
  const unitDisplay = unit.getDisplayString();

  const [active, setActive] = useState(false);
  const [isFocus, setIsFocus] = useState(false);
  const inputEl = useRef<HTMLInputElement>(null);
  const textAreaEl = useRef<HTMLTextAreaElement>(null);
  const displayValue =
    props.disabled && props.disabledDisplay
      ? props.disabledDisplay
      : props.type === NUMBER_VAL
        ? props.allowExponent
          ? valueAsExponentialDisplay(unit.toInput(Number(props.val)))
          : valueAsNumber(unit.toInput(Number(props.val)))
        : props.val;
  const [formControlVal, setFormControlVal] = useState<number | string>(displayValue);

  const ref = useOutsideClick(() => {
    setActive(false);
  });

  const resizeTextArea = () => {
    if (!props.isTextArea || !textAreaEl.current) {
      return;
    }

    textAreaEl.current.style.height = "auto";
    textAreaEl.current.style.height = `${textAreaEl.current.scrollHeight}px`;
  };

  const revertValue = () => {
    setFormControlVal(props.val);
    if (props.onUpdate) {
      if (props.type === NUMBER_VAL) {
        props.onUpdate(props.val, revertValue);
      } else {
        console.assert(props.type === TEXT_VAL);
        props.onUpdate(props.val, revertValue);
      }
    }
  };

  const pushNewValue = (newValue: string | number) => {
    if (props.onOldUpdateCallback) {
      props.onOldUpdateCallback("" + newValue);
    } else if (props.onUpdate) {
      if (props.type === NUMBER_VAL) {
        const parsed = Number(newValue);

        props.onUpdate(parsed, revertValue);
      } else {
        console.assert(props.type === TEXT_VAL);
        props.onUpdate(newValue.toString(), revertValue);
      }
      setFormControlVal(newValue);
    }
  };

  const handleValueChanged = (value: string | number) => {
    if (props.type === "number" && props.allowExponent) {
      // Leaving the field without editing must not store the rounded display value back
      if (String(value) === String(displayValue)) return;
      const parsed = parseExponentialInput(value, !!props.disableNegativeNumber);
      if (!Number.isFinite(parsed)) {
        revertValue();
        return;
      }
      pushNewValue(getUnitOrDefault(props.unit).fromInput(parsed));
      return;
    }
    if (props.type === "number") {
      let disablenegativeNumber = false;
      if (props.disableNegativeNumber !== undefined) {
        disablenegativeNumber = props.disableNegativeNumber;
      }
      value = removeInvalidTextFromNumberString(value, disablenegativeNumber, !!props.isInteger);

      if (typeof value === "string" && !value.match(validNumberRegex)) {
        value = "";
      }

      const unit = getUnitOrDefault(props.unit);
      const newVal = unit.fromInput(valueAsNumber(value));
      pushNewValue(newVal);
    } else {
      pushNewValue(value);
    }
  };

  useEffect(() => {
    if (!isFocus) {
      const unit = getUnitOrDefault(props.unit);
      let val = props.val;
      if (props.type === "number") {
        val = unit.toInput(Number(val));
        val = props.allowExponent ? valueAsExponentialDisplay(val) : valueAsNumber(val);
      }
      setFormControlVal(val);
    }
  }, [props.unit]);

  useEffect(() => {
    if (props.type === "number") {
      if (active && inputEl.current) {
        inputEl.current.focus();
        inputEl.current.select();
      }
    }
    if (props.type === "text") {
      if (active && props.isTextArea && textAreaEl.current) {
        textAreaEl.current.focus();
      } else if (active && inputEl.current) {
        inputEl.current.focus();
      }
    }
  }, [active, props.type, props.isTextArea]);

  useEffect(() => {
    const unit = getUnitOrDefault(props.unit);
    let val = props.val;
    if (props.type === "number") {
      val = unit.toInput(Number(val));
      val = props.allowExponent ? valueAsExponentialDisplay(val) : valueAsNumber(val);
    }
    setFormControlVal(val);
  }, [props.val]);

  useEffect(() => {
    resizeTextArea();
  }, [formControlVal, props.isTextArea, isFocus]);

  const editBorderCss = props.border ? "border-format" : "border-none";
  const editAlignCss = props.textAlign ? `table-text-align-${props.textAlign}` : "table-text-align-center";
  const editTypeCss = props.isTextArea ? "editfield-textarea" : "";
  const containerTypeCss = props.isTextArea ? "editfield-container-textarea" : "";
  const borderTypeCss = props.isTextArea ? "editfield-border-style-textarea" : "";
  const textareaBaseHeight = "calc(var(--universal-input-height) + 12px)";

  const editingValue = Number.isNaN(formControlVal) ? 0.0 : formControlVal;

  const editJsx = (
    <Form.Control
      size="sm"
      id={props.id ?? ""}
      data-testid={props.dataTestId ?? ""}
      as={props.isTextArea ? "textarea" : "input"}
      disabled={props.disabled}
      autoComplete="off"
      placeholder={props.placeholder ? props.placeholder : ""}
      ref={(el: HTMLInputElement | HTMLTextAreaElement | null) => {
        if (props.isTextArea) {
          textAreaEl.current = el as HTMLTextAreaElement | null;
          return;
        }

        inputEl.current = el as HTMLInputElement | null;
      }}
      onKeyDown={(e) => {
        if (e.key === "Enter" && !props.isTextArea) {
          inputEl.current?.blur();
          setActive(false);
          return true;
        }
        if (props.type === "number") {
          if ((props.disableNegativeNumber === undefined || props.disableNegativeNumber === false) && e.key === "-") {
            return true;
          }
          // exponent characters; "-" is needed for small numbers like 1e-3 even when negatives are disabled
          if (props.allowExponent && ["e", "E", "+", "-"].includes(e.key)) {
            return true;
          }

          switch (e.key) {
            case "0":
            case "1":
            case "2":
            case "3":
            case "4":
            case "5":
            case "6":
            case "7":
            case "8":
            case "9":
            case "ArrowLeft":
            case "ArrowRight":
            case "Tab":
            case "Backspace":
            case "Delete":
            case "Enter":
            case "Control":
            case "v":
            case "c":
              break;
            case ",":
            case ".":
              if (props.isInteger) {
                e.preventDefault();
                return false;
              }
              break;
            default:
              e.preventDefault();
              return false;
          }
        }
      }}
      type="text"
      onChange={(e) => {
        const value = e.target.value;
        let latestValue: string | number = value;

        if (props.type === "number") {
          let disablenegativeNumber = false;
          if (props.disableNegativeNumber !== undefined) {
            disablenegativeNumber = props.disableNegativeNumber;
          }

          const evalInp = props.allowExponent
            ? value.replace(/[^\d.,eE+-]/g, "")
            : removeInvalidTextFromNumberString(value, disablenegativeNumber);
          setFormControlVal(evalInp);
          latestValue = evalInp;
        } else {
          setFormControlVal(value);
        }

        if (props.isTextArea && e.target instanceof HTMLTextAreaElement) {
          e.target.style.height = "auto";
          e.target.style.height = `${e.target.scrollHeight}px`;
        }

        if (props.instantupdate) {
          pushNewValue(latestValue);
        }
      }}
      onFocus={() => {
        setIsFocus(true);
        setActive(true);
        setFormControlVal(displayValue);
      }}
      onBlur={() => {
        setIsFocus(false);
        setActive(false);
        handleValueChanged(formControlVal);
      }}
      value={isFocus ? editingValue : displayValue}
      className={`editfield ${editBorderCss} ${editAlignCss} ${editTypeCss}`}
      style={{
        fontSize: props.fontSize ? props.fontSize : "12px !important",
        paddingLeft: props.textAlign === "left" && !props.tableView ? "16px" : "8px",
        minHeight: props.isTextArea ? textareaBaseHeight : undefined,
        height: props.isTextArea ? textareaBaseHeight : undefined,
        borderRadius: props.isTextArea ? "6px" : undefined,
      }}
    />
  );

  const fontSizeStyle = { fontSize: props.fontSize || "12px" };

  const unitJsx =
    props.unit && props.displayUnit !== false ? (
      <InputGroup.Text className="editfield-unit" style={fontSizeStyle}>
        {unitDisplay}
      </InputGroup.Text>
    ) : null;

  const labelJsx = props.label ? (
    <InputGroup.Text className="editfield-label" style={{ ...fontSizeStyle, flex: "0 0 50%" }}>
      {props.label}
    </InputGroup.Text>
  ) : null;

  return (
    <div
      ref={ref}
      className={`editfield-container ${containerTypeCss}`}
      style={props.fullWidth ? { width: "100%" } : undefined}
      onClick={() => setActive(!active)}
    >
      <div className={props.tableView ? "table-view" : `editfield-border-style ${borderTypeCss}`}>
        <InputGroup className={active ? "active" : ""}>
          {labelJsx}
          {editJsx}
          {unitJsx}
        </InputGroup>
      </div>
    </div>
  );
};

export { removeInvalidTextFromNumberString, SXEditField, UnitCreator };
